import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import { Permission, hasPermission, PERMISSIONS } from '@/utils/permissions';
import { 
  canUserCreateWorkspace, 
  canUserEditWorkspace, 
  canUserDeleteWorkspace, 
  canUserViewWorkspace,
  isWorkspaceOwner,
  getWorkspacePermissions,
  MinimalWorkspace
} from '@/utils/workspacePermissions';
import { Workspace, WorkspaceListItem, BackendWorkspace, BackendWorkspaceWithVersion } from '@/types/workspace';

export const usePermissions = () => {
  const { user } = useAuth();

  // Memoize permission checking functions
  const hasPermissionCallback = useCallback((permission: Permission) => hasPermission(user, permission), [user]);

  // Auth permissions
  const canManageUsers = useCallback(() => hasPermission(user, PERMISSIONS.auth.all), [user]);
  const canRegisterUsers = useCallback(() => hasPermission(user, PERMISSIONS.auth.register), [user]);
  const canUpdateUsers = useCallback(() => hasPermission(user, PERMISSIONS.auth.update), [user]);

  // Site permissions
  const canViewSites = useCallback(() => hasPermission(user, PERMISSIONS.sites.list), [user]);
  const canCreateSite = useCallback(() => hasPermission(user, PERMISSIONS.sites.create), [user]);
  const canUpdateSite = useCallback(() => hasPermission(user, PERMISSIONS.sites.update), [user]);

  // Turbine permissions
  const canViewTurbines = useCallback(() => hasPermission(user, PERMISSIONS.turbines.list), [user]);
  const canCreateTurbine = useCallback(() => hasPermission(user, PERMISSIONS.turbines.create), [user]);
  const canUpdateTurbine = useCallback(() => hasPermission(user, PERMISSIONS.turbines.update), [user]);

  // Failure permissions
  const canViewFailures = useCallback(() => hasPermission(user, PERMISSIONS.failures.list), [user]);
  const canCreateFailure = useCallback(() => hasPermission(user, PERMISSIONS.failures.create), [user]);
  const canUpdateFailure = useCallback(() => hasPermission(user, PERMISSIONS.failures.update), [user]);

  // Workspace permissions
  const canCreateWorkspace = useCallback(() => canUserCreateWorkspace(user), [user]);
  const canViewWorkspace = useCallback((workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace) => 
    canUserViewWorkspace(user, workspace), [user]);
  const canEditWorkspace = useCallback((workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace) => 
    canUserEditWorkspace(user, workspace), [user]);
  const canDeleteWorkspace = useCallback((workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace) => 
    canUserDeleteWorkspace(user, workspace), [user]);
  const isWorkspaceOwnerCallback = useCallback((workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace) => 
    isWorkspaceOwner(user, workspace), [user]);
  const getWorkspacePermissionsCallback = useCallback((workspace: Workspace | WorkspaceListItem | BackendWorkspace | BackendWorkspaceWithVersion | MinimalWorkspace) => 
    getWorkspacePermissions(user, workspace), [user]);

  // Role checks
  const isAdmin = useCallback(() => user?.role === 'admin', [user]);
  const isOnyxUser = useCallback(() => user?.company === 'onyx', [user]);
  const isOnyxAdmin = useCallback(() => user?.company === 'onyx' && user?.role === 'admin', [user]);
  const isCustomerAdmin = useCallback(() => user?.company === 'company' && user?.role === 'admin', [user]);

  // User info - memoized to prevent unnecessary re-renders
  const userInfo = useMemo(() => ({
    user,
    userRole: user?.role || 'user',
    userCompany: user?.company || 'company',
    userId: user?.id || '',
  }), [user]);

  return useMemo(() => ({
    // Check a specific permission
    hasPermission: hasPermissionCallback,

    // Auth permissions
    canManageUsers,
    canRegisterUsers,
    canUpdateUsers,

    // Site permissions
    canViewSites,
    canCreateSite,
    canUpdateSite,

    // Turbine permissions
    canViewTurbines,
    canCreateTurbine,
    canUpdateTurbine,

    // Failure permissions
    canViewFailures,
    canCreateFailure,
    canUpdateFailure,

    // Workspace permissions
    canCreateWorkspace,
    canViewWorkspace,
    canEditWorkspace,
    canDeleteWorkspace,
    isWorkspaceOwner: isWorkspaceOwnerCallback,
    getWorkspacePermissions: getWorkspacePermissionsCallback,

    // Role checks
    isAdmin,
    isOnyxUser,
    isOnyxAdmin,
    isCustomerAdmin,

    // User info
    ...userInfo,
  }), [
    hasPermissionCallback,
    canManageUsers,
    canRegisterUsers,
    canUpdateUsers,
    canViewSites,
    canCreateSite,
    canUpdateSite,
    canViewTurbines,
    canCreateTurbine,
    canUpdateTurbine,
    canViewFailures,
    canCreateFailure,
    canUpdateFailure,
    canCreateWorkspace,
    canViewWorkspace,
    canEditWorkspace,
    canDeleteWorkspace,
    isWorkspaceOwnerCallback,
    getWorkspacePermissionsCallback,
    isAdmin,
    isOnyxUser,
    isOnyxAdmin,
    isCustomerAdmin,
    userInfo,
  ]);
};
